import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../../store";
import { updateProfessionalInfo } from "../../../../redux/slices/userProfileSlice";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";

import { ArrowLeft } from "lucide-react";
import Logo from "../../../../components/Logo/Logo";

import { Admin_WorkTypes_LIST } from "../../../../constants/adminData";
import type { WorkType } from "../../../../types/admin";

interface Props {
  onContinue: () => void;
  onBack: () => void;
}

export default function WorkType({ onContinue, onBack }: Props) {
  const dispatch = useDispatch();
  const profile = useAppSelector((s) => s.userProfile);

  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [workType, setWorkType] = useState(""); // store ID as string
  const [loading, setLoading] = useState(true);

  const initialized = useRef(false);

  //  LOAD API
  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        const res = await Admin_WorkTypes_LIST();

        const safeData = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [];

        if (!ignore) setWorkTypes(safeData);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, []);

  //  PREFILL 
  useEffect(() => {
    if (!initialized.current && profile.professionalInfo?.workType) {
      initialized.current = true;

      setWorkType(String(profile.professionalInfo.workType)); // ✅ convert to string
    }
  }, [profile]);

  //  GET NAME FROM ID 
  const getWorkTypeName = (id: string) => {
    return (
      workTypes.find((w) => String(w.workTypeId) === id)?.workTypeName || ""
    );
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();

    if (!workType) return;

    dispatch(
      updateProfessionalInfo({
        workType: Number(workType), 
      })
    );

    onContinue();
  };

  if (loading) return <p className="text-center">Loading...</p>;

  return (
    <div className="h-screen bg-[#F5F5F5] flex flex-col items-center px-6 pt-4 pb-12 relative">
      <div className="w-full max-w-xl flex flex-col items-center flex-1">

       
        <button
          onClick={onBack}
          className="absolute left-6 top-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center z-10"
        >
          <ArrowLeft className="w-6 h-6 text-brand-primary" />
        </button>

        <div className="flex-1 w-full flex flex-col">
          <div className="flex justify-center mb-0">
            <Logo />
          </div>

          <h1 className="text-[26px] md:text-3xl lg:text-4xl font-semibold tracking-wide text-center mb-8">
            Work Type
          </h1>

          <form
            onSubmit={handleContinue}
            className="w-full max-w-xl flex flex-col flex-1"
          >

            <div className="space-y-6">

              
              <div
                className={`rounded-lg ${
                  workType
                    ? "bg-gradient-to-r from-brand-primary to-brand-gray p-[2px]"
                    : ""
                }`}
              >
                <Select value={workType} onValueChange={setWorkType}>
                  <SelectTrigger
                    className={`w-full bg-white h-12 md:h-14 text-base md:text-lg lg:text-xl px-4 ${
                      workType
                        ? "border-0 rounded-[6px]"
                        : "border-2 rounded-lg border-gray-300"
                    }`}
                  >
                   
                    <SelectValue
                      placeholder={
                        workType
                          ? getWorkTypeName(workType)
                          : "Select Work Type"
                      }
                    />
                  </SelectTrigger>

                  <SelectContent className="max-h-[220px] overflow-y-auto z-50 bg-white">
                    {workTypes.map((w) => (
                      <SelectItem
                        key={w.workTypeId}
                        value={String(w.workTypeId)} 
                      >
                        {w.workTypeName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

          
            <div className="mt-auto pt-10 pb-4 w-full">
              <button
                type="submit"
                disabled={!workType}
                className="w-full bg-brand-primary hover:bg-brand-hover text-white h-12 md:h-14 lg:h-16 text-lg md:text-xl lg:text-2xl font-medium rounded-lg disabled:bg-gray-300"
              >
                Continue
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}